/**
 * PAEContext — global "what entity is the user currently focused on?"
 * channel for the floating PAE button.
 *
 * Pages that show a single record in a way the URL doesn't capture
 * (e.g. inline-expanded rows on Orders / Requisitions / Patients)
 * push their context here when the record is opened and clear it
 * when the record is closed. The PAE modal reads this context to
 * pre-fill the "Attach to" fields so the user doesn't have to type
 * the order number themselves.
 *
 * Usage from a page:
 *
 *   import { usePAEContextSetter } from '../../context/PAEContext';
 *   const setPAE = usePAEContextSetter();
 *   useEffect(() => {
 *     if (expandedId) setPAE({ order_id: expandedId, label: `Order ${num}` });
 *     else setPAE(null);
 *     return () => setPAE(null);
 *   }, [expandedId, setPAE]);
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const PAECtx = createContext({
  context: null,
  setContext: () => {},
});

export function PAEContextProvider({ children }) {
  const [context, setContextState] = useState(null);

  const setContext = useCallback((next) => {
    // Pass null/undefined or an empty object to clear.
    if (!next || (typeof next === 'object' && Object.keys(next).length === 0)) {
      setContextState(null);
    } else {
      setContextState(next);
    }
  }, []);

  const value = useMemo(() => ({ context, setContext }), [context, setContext]);
  return <PAECtx.Provider value={value}>{children}</PAECtx.Provider>;
}

export function usePAEContext() {
  return useContext(PAECtx).context;
}

export function usePAEContextSetter() {
  return useContext(PAECtx).setContext;
}
